/**
 * Repository Connect Form Component
 *
 * Form for connecting new code repositories (GitHub, GitLab, Bitbucket)
 *
 * Features:
 * - Provider selection (GitHub, GitLab, Bitbucket)
 * - OAuth/Personal Access Token authentication
 * - Repository URL validation
 * - Connection testing
 *
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import React, { useState } from 'react';
import { Button, Label, TextInput, Select, Alert, Spinner } from 'flowbite-react';
import { HiCheckCircle, HiXCircle } from 'react-icons/hi';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface RepositoryConnectFormProps {
  clientId: string;
  companyId: string;
  projectId?: string; // Optional: Link repository to specific project
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const RepositoryConnectForm: React.FC<RepositoryConnectFormProps> = ({
  clientId,
  companyId,
  projectId,
  onSuccess,
  onCancel
}) => {
  const [provider, setProvider] = useState<string>('github');
  const [repositoryUrl, setRepositoryUrl] = useState<string>('');
  const [accessToken, setAccessToken] = useState<string>('');
  const [repositoryName, setRepositoryName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTestConnection = async () => {
    if (!repositoryUrl || !accessToken) {
      setError('Repository URL and access token are required');
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);
      setError(null);

      // TODO: /api/repositories/test endpoint doesn't exist in backend
      // Using REPOSITORIES.LIST with a validation call as workaround
      // Consider implementing a dedicated test endpoint in the backend
      const response = await Make_Authorized_API_Call<{ Repository_Name?: string }>(
        API_ENDPOINTS.REPOSITORIES.LIST,
        'POST',
        {
          Provider: provider,
          Repository_URL: repositoryUrl,
          Access_Token: accessToken,
          Client_ID: clientId,
          Company_ID: companyId,
          Test_Connection: true, // Flag for backend to validate without creating
        }
      );

      if (isSuccessResponse(response)) {
        setTestResult({
          success: true,
          message: 'Connection successful! Repository is accessible.'
        });

        // Auto-fill repository name if available
        if (response.data?.Records?.[0]?.Repository_Name) {
          setRepositoryName(response.data.Records[0].Repository_Name);
        }
      } else {
        setTestResult({
          success: false,
          message: response.message || 'Connection test failed'
        });
      }
    } catch (err) {
      console.error('[RepositoryConnectForm] Error testing connection:', err);
      setTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to test connection'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!repositoryUrl || !accessToken || !repositoryName) {
      setError('All fields are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.REPOSITORIES.CREATE,
        'POST',
        {
          Provider: provider,
          Repository_URL: repositoryUrl,
          Repository_Name: repositoryName,
          Access_Token: accessToken,
          Client_ID: clientId,
          Company_ID: companyId,
          Project_ID: projectId, // Optional: Link to specific project
        }
      );

      if (isSuccessResponse(response)) {
        // Success
        onSuccess?.();
      } else {
        throw new Error(response.message || 'Failed to connect repository');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect repository';
      console.error('[RepositoryConnectForm] Error connecting repository:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Provider Selection */}
      <div>
        <Label htmlFor="provider" value="Repository Provider" />
        <Select
          id="provider"
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          required
        >
          <option value="github">GitHub</option>
          <option value="gitlab">GitLab</option>
          <option value="bitbucket">Bitbucket</option>
        </Select>
      </div>

      {/* Repository URL */}
      <div>
        <Label htmlFor="repositoryUrl" value="Repository URL" />
        <TextInput
          id="repositoryUrl"
          type="url"
          placeholder="https://github.com/username/repository"
          value={repositoryUrl}
          onChange={(e) => setRepositoryUrl(e.target.value)}
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Full URL to your repository (e.g., https://github.com/username/repo)
        </p>
      </div>

      {/* Access Token */}
      <div>
        <Label htmlFor="accessToken" value="Personal Access Token" />
        <TextInput
          id="accessToken"
          type="password"
          placeholder="ghp_xxxxxxxxxxxx"
          value={accessToken}
          onChange={(e) => setAccessToken(e.target.value)}
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          {provider === 'github' && 'Generate at: Settings > Developer settings > Personal access tokens'}
          {provider === 'gitlab' && 'Generate at: Settings > Access Tokens'}
          {provider === 'bitbucket' && 'Generate at: Personal settings > App passwords'}
        </p>
      </div>

      {/* Test Connection Button */}
      <div>
        <Button
          type="button"
          color="light"
          onClick={handleTestConnection}
          disabled={testing || !repositoryUrl || !accessToken}
          className="w-full"
        >
          {testing ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Testing Connection...
            </>
          ) : (
            'Test Connection'
          )}
        </Button>
      </div>

      {/* Test Result */}
      {testResult && (
        <Alert
          color={testResult.success ? 'success' : 'failure'}
          icon={testResult.success ? HiCheckCircle : HiXCircle}
        >
          {testResult.message}
        </Alert>
      )}

      {/* Repository Name */}
      <div>
        <Label htmlFor="repositoryName" value="Repository Display Name" />
        <TextInput
          id="repositoryName"
          type="text"
          placeholder="My Project Repository"
          value={repositoryName}
          onChange={(e) => setRepositoryName(e.target.value)}
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Friendly name to identify this repository
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert color="failure">
          {error}
        </Alert>
      )}

      {/* Form Actions */}
      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          color="blue"
          disabled={loading || !testResult?.success}
          className="flex-1"
        >
          {loading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Connecting...
            </>
          ) : (
            'Connect Repository'
          )}
        </Button>
        <Button
          type="button"
          color="light"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
      </div>

      {/* Help Text */}
      <div className="pt-2 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          <strong>Note:</strong> Test the connection before submitting. The access token requires read access to repository content and metadata.
        </p>
      </div>
    </form>
  );
};
